import qs from 'qs';
import { removePropertyHasEmptyValue } from '@/utils/object';

/**
 * URLクエリパラメータを vue-router を経由せず window.location / history.replaceState で
 * 直接読み書きする composable。旧 mixin `@/mixins/query_parameter` の置き換え。
 *
 * 現在の利用箇所は `pages/user/CallAnalytics.vue` のみ。
 *
 * FIXME: 新規に使わないこと。replaceStateで書いた内容はvue-routerに検知されないため
 * route.queryが実URLと乖離し、同一ロケーションへのrouter.pushがduplicatedで握り潰される
 * といった不具合につながる。リード一覧は元々これを使っていたが、その乖離が原因で
 * 「一度閉じたリードを開き直せない」不具合が出たためvue-routerに移行済み。
 * クエリの読み書きはrouterで行い、変化への反応はwatchで拾うこと。
 *
 * routerに反応する単一キーのreactiveな値が欲しい場合は
 * `@/composable/query-parameter`(`useQueryParameter`)を使う。
 */
const replaceStateWithQueryObject = (queryObject: Record<string, unknown>) => {
  // 空のものを除外したObject
  const pureQueryObject = {};
  for (const key in queryObject) {
    pureQueryObject[key] = removePropertyHasEmptyValue(queryObject[key]);
  }
  const newQuery = Object.keys(pureQueryObject).length > 0 ? `?${qs.stringify(pureQueryObject)}` : '';
  history.state.current = location.pathname + newQuery;
  if (newQuery) {
    history.replaceState(history.state, '', newQuery);
  } else {
    history.replaceState(history.state, '', location.pathname);
  }
};

// qs.parse時に使用するdecoder
// Booleanのparseなどが最適化される
// 詳細は https://github.com/ljharb/qs/issues/91#issuecomment-437926409
const parseOptionsForQs = (): qs.IParseOptions => {
  return {
    decoder(str, decoder, charset) {
      const strWithoutPlus = str.replace(/\+/g, ' ');
      if (charset === 'iso-8859-1') {
        // unescape never throws, no try...catch needed:
        return strWithoutPlus.replace(/%[0-9a-f]{2}/gi, unescape);
      }

      // MEMO: 数値のみのクエリパラメタをIntegerに変換しているが、
      // パーサに求める範囲を超えているためコメントアウトする
      // クエリパラメタに数値を入れて数値型を取り出したい場合は取り出した後で明示的にparseIntかけることとする
      // 例.) LeadList.vueのcreated内、currentDisplayNumberやcurrentPageに対する部分
      // if (/^(\d+|\d*\.\d+)$/.test(str)) {
      //   return parseFloat(str)
      // }

      const keywords = {
        // true: true,
        // false: false,
        // null: null,
        // undefined
      };
      if (str in keywords) {
        return keywords[str];
      }

      // utf-8
      try {
        return decodeURIComponent(strWithoutPlus);
      } catch (e) {
        return strWithoutPlus;
      }
    },
  };
};

const getCurrentQueryParameter = () => {
  return qs.parse(window.location.search.substring(1), parseOptionsForQs()) || {};
};

const getCallAnalyticsQueryParameter = () => {
  const query = qs.parse(window.location.search.substring(1), parseOptionsForQs());
  return query.callAnalytics;
};

const setQueryParameter = (queryObject: Record<string, unknown>): void => {
  const newQueryObject = {
    ...getCurrentQueryParameter(),
    ...queryObject,
  };
  replaceStateWithQueryObject(newQueryObject);
};

const useUrlQueryParameter = () => {
  return {
    getCallAnalyticsQueryParameter,
    setQueryParameter,
  };
};

export {
  useUrlQueryParameter,
};
